import { Select } from '@civilgrid/ui/components';
import { useMemo } from 'react';

/** Radix Select reserves the empty string, so "no year filter" needs a sentinel value. */
const ALL_YEARS = 'all';

type YearsFilterDropdownProps = {
  /** Selectable years, already sorted. */
  years: string[];
  /** The selected year, or null when no year filter is applied. */
  value: string | null;
  onChange: (year: string | null) => void;
};

/**
 * Toolbar dropdown filtering the Conflict Summary table to a single
 * construction year. "All Years" clears the year filter.
 */
export function YearsFilterDropdown({ years, value, onChange }: YearsFilterDropdownProps) {
  // A persisted filter can reference a year missing from the aggregate options
  // (e.g. its last project was re-dated); keep it selectable so the trigger
  // still displays the active filter.
  const options = useMemo(() => {
    const selectableYears = value && !years.includes(value) ? [...years, value].sort() : years;
    return [{ value: ALL_YEARS, label: 'All Years' }, ...selectableYears.map((year) => ({ value: year, label: year }))];
  }, [years, value]);

  return (
    <Select
      options={options}
      value={value ?? ALL_YEARS}
      onValueChange={(next) => onChange(next === ALL_YEARS ? null : next)}
      aria-label="Construction year filter"
      triggerClassName="h-6 w-auto min-w-24 gap-1 text-xs"
      data-testid="conflicts-summary-years-filter"
    />
  );
}
